import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "./adminApi";

const DOC_LABELS = {
  cv: "CV",
  id_card: "Pièce d'identité",
  id_card_recto: "Pièce d'identité (recto)",
  id_card_verso: "Pièce d'identité (verso)",
  carte_sejour: "Carte de séjour",
  carte_sejour_recto: "Carte de séjour (recto)",
  carte_sejour_verso: "Carte de séjour (verso)",
  attestation_secu: "Attestation Sécu",
  rib: "RIB",
  justificatif_domicile: "Justificatif de domicile",
  certificat_travail: "Certificat de travail / bulletins",
  visite_medicale: "Visite médicale",
  rqth: "RQTH",
  caces_habilitations: "CACES / habilitations",
  other: "Autre",
};

/**
 * Dialog to send an email to a candidate listing their rejected documents
 * (with rejection reason) + an optional custom message.
 *
 * Props:
 *  - candidate: { user_id, name, email, documents: [...] }
 *  - open, onOpenChange
 *  - onSent(): callback fired after successful send
 */
export default function RejectedDocsEmailDialog({ candidate, open, onOpenChange, onSent }) {
  const rejectedDocs = useMemo(() => {
    return (candidate?.documents || []).filter((d) => d.validation_status === "rejected");
  }, [candidate?.documents]);

  const [selectedIds, setSelectedIds] = useState(() => rejectedDocs.map((d) => d.doc_id));
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Sync when dialog opens or list changes
  useEffect(() => {
    if (open) {
      setSelectedIds(rejectedDocs.map((d) => d.doc_id));
      setMessage("");
    }
  }, [rejectedDocs, open]);

  const toggle = (docId) => {
    setSelectedIds((prev) => prev.includes(docId) ? prev.filter((i) => i !== docId) : [...prev, docId]);
  };

  const send = async () => {
    if (selectedIds.length === 0) {
      toast.error("Sélectionnez au moins une pièce refusée");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/candidates/${candidate.user_id}/request-rejected-documents`, {
        doc_ids: selectedIds,
        message: message.trim(),
      });
      if (r.data?.sent) {
        toast.success(`Email envoyé (${r.data.count} pièce${r.data.count > 1 ? "s" : ""} refusée${r.data.count > 1 ? "s" : ""})`);
        onSent?.();
        onOpenChange(false);
      } else {
        toast.info(r.data?.reason || "Aucune pièce refusée à signaler");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur envoi email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-none" data-testid="rejected-docs-email-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#3F3F3F] flex items-center gap-2">
            <Mail className="w-4 h-4 text-red-600" />
            Notifier le candidat des pièces refusées
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {rejectedDocs.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Aucune pièce n&apos;est marquée comme refusée pour ce candidat. Refusez d&apos;abord un document depuis la prévisualisation.
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-600">
                Cochez les pièces refusées à inclure dans l&apos;email de rappel envoyé à
                {" "}<strong className="text-[#3F3F3F]">{candidate?.email || candidate?.name || "ce candidat"}</strong>.
                Le motif de refus (saisi lors de la validation) est repris automatiquement dans l&apos;email.
              </p>

              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 divide-y divide-gray-100" data-testid="rejected-docs-list">
                {rejectedDocs.map((d) => {
                  const checked = selectedIds.includes(d.doc_id);
                  const label = DOC_LABELS[d.doc_type] || d.doc_type;
                  const reason = (d.validation_comment || "").trim();
                  return (
                    <label
                      key={d.doc_id}
                      className={`flex items-start gap-2 p-2 cursor-pointer ${checked ? "bg-red-50" : "bg-white hover:bg-gray-50"}`}
                      data-testid={`reject-doc-item-${d.doc_id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(d.doc_id)}
                        className="mt-0.5"
                        data-testid={`reject-doc-check-${d.doc_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="rounded-none text-[10px] bg-red-100 text-red-700 border border-red-300">✗ Refusé</Badge>
                          <span className="text-sm font-semibold text-[#3F3F3F] truncate">{label}</span>
                          <span className="text-[10px] text-gray-500 truncate">{d.original_filename}</span>
                        </div>
                        {reason ? (
                          <p className="text-[11px] text-red-700 mt-1"><strong>Motif :</strong> {reason}</p>
                        ) : (
                          <p className="text-[11px] text-amber-700 mt-1 italic">Aucun motif renseigné — modifiez le document pour ajouter un motif si nécessaire.</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div>
                <Label className="text-xs font-semibold text-gray-700 mb-1 block">Message personnalisé (facultatif)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Ex: Merci de bien vouloir déposer une nouvelle version avant vendredi..."
                  className="rounded-none text-sm"
                  data-testid="reject-email-message"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  onClick={send}
                  disabled={sending || selectedIds.length === 0}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-none flex-1 font-semibold"
                  data-testid="reject-email-send"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? "Envoi..." : `Envoyer l'email (${selectedIds.length})`}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} className="rounded-none">
                  Annuler
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
